// Payments & Milestones — PRD v2 spec:
//   6 KPI cards: Total Contracted, Collected, Outstanding, Invoiced, Overdue,
//                Collected This Month
//   Revenue Summary with per-project progress bars
//   Overdue Escalation banner (only when overdue exists)
//   Cash Flow Forecast: 0-30 / 31-60 / 61-90 buckets
//   Calendar view (existing)
//   All Milestones grouped by project, collapsible, filterable, exportable
// Read-only — all editing happens on the project detail page.
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Loader2, ExternalLink, ChevronLeft, ChevronRight, ChevronDown, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import { fetchJson, fmtMoney, fmtMoneyShort, fmtDate, SectionHeader } from "./pccHelpers";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "invoiced", label: "Invoiced" },
  { id: "overdue", label: "Overdue" },
  { id: "paid", label: "Received" },
];

export default function PccPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await fetchJson("/planning-command-center/payments-dashboard");
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const ms = data?.milestones || [];
    if (filter === "all") return ms;
    if (filter === "overdue") return ms.filter(m => m.is_overdue);
    return ms.filter(m => (m.status || "").toLowerCase() === filter);
  }, [data, filter]);

  // Group milestones by program
  const groupedByProgram = useMemo(() => {
    const byProg = {};
    filtered.forEach(m => {
      const key = m.program_id || "_unknown";
      if (!byProg[key]) byProg[key] = { program_id: m.program_id, program_name: m.program_name, client_name: m.client_name, items: [] };
      byProg[key].items.push(m);
    });
    return Object.values(byProg).sort((a, b) => (a.program_name || "").localeCompare(b.program_name || ""));
  }, [filtered]);

  // Revenue summary per project (top 8 by contract value)
  const revenueSummary = useMemo(() => {
    const byProg = {};
    (data?.milestones || []).forEach(m => {
      const key = m.program_id;
      if (!byProg[key]) byProg[key] = { program_id: m.program_id, program_name: m.program_name, client_name: m.client_name, total: 0, collected: 0 };
      byProg[key].total += m.amount;
      if (m.status === "paid") byProg[key].collected += m.amount;
    });
    return Object.values(byProg).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [data]);

  function copyAll() {
    const lines = filtered.map(m => `${m.program_name}\t${m.title}\t${m.amount}\t${m.due_date}\t${m.is_overdue ? "OVERDUE" : (m.status || "pending").toUpperCase()}`).join("\n");
    navigator.clipboard.writeText(`Project\tMilestone\tAmount\tDue\tStatus\n${lines}`);
    toast.success(`Copied ${filtered.length} milestones to clipboard`);
  }

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;
  if (error) return <Card className="bg-red-500/[0.06] border-red-500/30 p-4 text-red-200">{error}</Card>;
  if (!data) return null;
  const k = data.kpis;
  const buckets = data.forecast_buckets || { "0_30": 0, "31_60": 0, "61_90": 0 };

  return (
    <div className="space-y-5" data-testid="pcc-payments">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-light text-white">Payments & Milestones</h2>
          <p className="text-white/55 text-sm">Cash collected, outstanding, and forecast across all active programs. Edit on project detail.</p>
        </div>
        <Button asChild variant="outline" size="sm" className="border-white/15 bg-white/[0.04]">
          <a href="/money" target="_blank" rel="noreferrer">Open Money Module <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></a>
        </Button>
      </div>

      {/* 6 KPI strip — PRD spec */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Total Contracted" value={k.total_contract} tone="white" />
        <Kpi label="Collected" value={k.total_collected} tone="emerald" />
        <Kpi label="Outstanding" value={k.total_outstanding} tone="amber" />
        <Kpi label="Invoiced" value={k.total_invoiced} tone="blue" />
        <Kpi label="Overdue" value={k.total_overdue} tone="red" />
        <Kpi label="Collected This Month" value={k.collected_this_month || 0} tone="emerald" />
      </div>

      {/* Overdue Escalation — only when overdue exists */}
      {k.total_overdue > 0 && (
        <Card className="bg-red-500/[0.06] border-red-500/30 p-4" data-testid="pcc-overdue-escalation">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-red-300">Overdue Escalation</div>
              <div className="text-sm text-white mt-1"><span className="font-semibold tabular-nums text-red-300">{fmtMoney(k.total_overdue)}</span> across multiple programs requires immediate follow-up.</div>
            </div>
            <Button onClick={() => setFilter("overdue")} variant="outline" size="sm" className="border-red-500/40 bg-red-500/[0.1] text-red-200 hover:bg-red-500/20">Review overdue ↓</Button>
          </div>
        </Card>
      )}

      {/* Revenue Summary + Cash Flow Forecast — side by side */}
      <div className="grid gap-5" style={{ gridTemplateColumns: "1fr 380px" }}>
        <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-revenue-summary">
          <SectionHeader dot="#2E7D55" title="Revenue Summary" />
          <div className="p-5 space-y-2.5">
            {revenueSummary.length === 0 ? (
              <div className="text-white/40 text-sm">No projects with milestones.</div>
            ) : revenueSummary.map(r => {
              const pct = r.total > 0 ? Math.min(100, (r.collected / r.total) * 100) : 0;
              return (
                <Link to={`/planning-command-center/projects/${r.program_id}`} key={r.program_id} className="block hover:bg-white/[0.02] -mx-5 px-5 py-1.5">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-[13px] text-white truncate flex-1">{r.program_name}</div>
                    <div className="text-[11px] text-white/55 font-mono tabular-nums shrink-0">{fmtMoneyShort(r.collected)} / {fmtMoneyShort(r.total)}</div>
                    <div className="text-[11px] text-emerald-300 font-mono tabular-nums w-12 text-right">{pct.toFixed(0)}%</div>
                  </div>
                  <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>

        <div className="space-y-5">
          <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-cashflow">
            <SectionHeader dot="#8FA79B" title="Cash Flow Forecast" />
            <div className="p-5 space-y-3">
              <ForecastRow label="0–30 days" value={buckets["0_30"]} max={Math.max(buckets["0_30"], buckets["31_60"], buckets["61_90"], 1)} color="bg-emerald-400" />
              <ForecastRow label="31–60 days" value={buckets["31_60"]} max={Math.max(buckets["0_30"], buckets["31_60"], buckets["61_90"], 1)} color="bg-amber-400" />
              <ForecastRow label="61–90 days" value={buckets["61_90"]} max={Math.max(buckets["0_30"], buckets["31_60"], buckets["61_90"], 1)} color="bg-blue-400" />
              <div className="pt-2 border-t border-white/[0.06] flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-white/40">Total 90-day pipeline</span>
                <span className="text-sm font-mono tabular-nums text-white">{fmtMoney(buckets["0_30"] + buckets["31_60"] + buckets["61_90"])}</span>
              </div>
            </div>
          </Card>
          <Card className="bg-[#000000] border-white/[0.08] p-5">
            <div className="text-[10px] uppercase tracking-wider text-white/40">Due This Month</div>
            <div className="text-2xl font-light text-amber-300 tabular-nums mt-1">{fmtMoney(data.due_this_month)}</div>
          </Card>
        </div>
      </div>

      {/* Payment Calendar */}
      <PaymentCalendar />

      {/* Grouped milestones */}
      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-milestones-list">
        <div className="flex items-center justify-between flex-wrap gap-3 px-6 py-4 border-b border-white/[0.08]">
          <div className="flex items-center gap-2.5">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
            <div className="text-[12px] uppercase tracking-[0.08em] font-semibold text-white">All Milestones ({filtered.length})</div>
          </div>
          <div className="flex gap-1.5 flex-wrap items-center">
            {FILTERS.map(f => (
              <Button key={f.id} variant={filter === f.id ? "default" : "outline"} size="sm" className={filter === f.id ? "bg-amber-500 hover:bg-amber-600 text-black border-amber-500" : "border-white/10 bg-white/[0.02] text-white/65"} onClick={() => setFilter(f.id)} data-testid={`pcc-pay-filter-${f.id}`}>
                {f.label}
              </Button>
            ))}
            <Button onClick={copyAll} variant="outline" size="sm" className="border-white/10 bg-white/[0.02] text-white/65 ml-1" data-testid="pcc-pay-export"><Copy className="w-3 h-3 mr-1" />Export</Button>
          </div>
        </div>
        <div className="divide-y divide-white/[0.06] max-h-[700px] overflow-y-auto">
          {groupedByProgram.length === 0 ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm">No milestones for this filter.</div>
          ) : groupedByProgram.map(g => {
            const isOpen = expanded[g.program_id] !== false; // default open
            const total = g.items.reduce((s, m) => s + m.amount, 0);
            const collected = g.items.filter(m => m.status === "paid").reduce((s, m) => s + m.amount, 0);
            return (
              <div key={g.program_id}>
                <div onClick={() => setExpanded({ ...expanded, [g.program_id]: !isOpen })} className="px-6 py-3 flex items-center gap-3 cursor-pointer hover:bg-white/[0.02]">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-white/45" /> : <ChevronRight className="w-4 h-4 text-white/45" />}
                  <Link to={`/planning-command-center/projects/${g.program_id}`} className="text-sm font-semibold text-white hover:text-amber-300 truncate flex-1" onClick={(e) => e.stopPropagation()}>{g.program_name}</Link>
                  <span className="text-[11px] text-white/45 font-mono">{g.items.length} milestones</span>
                  <span className="text-[12px] text-white/65 font-mono tabular-nums w-32 text-right">{fmtMoneyShort(collected)} / {fmtMoneyShort(total)}</span>
                </div>
                {isOpen && g.items.map(m => (
                  <div key={m.id} className="px-6 py-2.5 pl-12 flex items-center gap-4 bg-white/[0.01]">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{m.title || "Milestone"}</div>
                    </div>
                    <div className="text-[11px] text-white/45 font-mono w-24 text-right">{fmtDate(m.due_date)}</div>
                    <Badge className={`border ${m.is_overdue ? "bg-red-500/15 text-red-300 border-red-500/30" : (m.status === "paid") ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : (m.status === "invoiced") ? "bg-blue-500/15 text-blue-300 border-blue-500/30" : "bg-white/[0.06] text-white/55 border-white/15"}`}>{m.is_overdue ? "OVERDUE" : (m.status || "pending").toUpperCase()}</Badge>
                    <div className={`text-sm font-mono tabular-nums w-28 text-right ${m.is_overdue ? "text-red-300" : (m.status === "paid") ? "text-emerald-300" : "text-white"}`}>{fmtMoney(m.amount)}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function Kpi({ label, value, tone = "white" }) {
  const toneCls = { white: "text-white", emerald: "text-emerald-300", amber: "text-amber-300", red: "text-red-300", blue: "text-blue-300" }[tone];
  return (
    <Card className="bg-[#000000] border-white/[0.08] p-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-white/40">{label}</div>
      <div className={`mt-2 text-lg font-semibold tabular-nums ${toneCls}`}>{fmtMoney(value)}</div>
    </Card>
  );
}

function ForecastRow({ label, value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-white/55 font-mono">{label}</span>
        <span className="text-[12px] text-white tabular-nums font-mono">{fmtMoney(value)}</span>
      </div>
      <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Payment Calendar (preserved from Phase 3) ──────────────────────────
function PaymentCalendar() {
  const today = new Date();
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchJson(`/pcc/payment-calendar`, { year: cursor.year, month: cursor.month })
      .then(r => { if (alive) setItems(r.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [cursor.year, cursor.month]);

  const monthName = new Date(cursor.year, cursor.month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const firstDay = new Date(cursor.year, cursor.month - 1, 1).getDay();
  const daysInMonth = new Date(cursor.year, cursor.month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function step(delta) {
    let { year, month } = cursor;
    month += delta;
    if (month < 1) { month = 12; year--; }
    if (month > 12) { month = 1; year++; }
    setCursor({ year, month });
  }

  const itemsByDay = {};
  items.forEach(it => {
    const dt = new Date(it.due_date);
    if (dt.getFullYear() === cursor.year && dt.getMonth() + 1 === cursor.month) {
      const d = dt.getDate();
      if (!itemsByDay[d]) itemsByDay[d] = [];
      itemsByDay[d].push(it);
    }
  });

  return (
    <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-pay-calendar">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
        <div className="flex items-center gap-2.5">
          <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
          <div className="text-[12px] uppercase tracking-[0.08em] font-semibold text-white">Payment Calendar — {monthName}</div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => step(-1)} data-testid="pcc-cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => setCursor({ year: today.getFullYear(), month: today.getMonth() + 1 })}>Today</Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => step(1)} data-testid="pcc-cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-7 gap-1.5 text-[10px] uppercase tracking-wider text-white/40 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d} className="text-center font-semibold">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => {
            const isToday = d && cursor.year === today.getFullYear() && cursor.month === today.getMonth() + 1 && d === today.getDate();
            const dayItems = d ? (itemsByDay[d] || []) : [];
            return (
              <div key={i} className={`min-h-[72px] p-1.5 rounded border ${isToday ? "border-amber-400/60 bg-amber-500/[0.05]" : "border-white/[0.05] bg-white/[0.02]"} ${d ? "" : "opacity-0"}`}>
                {d && (
                  <>
                    <div className={`text-[10px] font-mono ${isToday ? "text-amber-300 font-bold" : "text-white/55"}`}>{d}</div>
                    <div className="mt-1 space-y-0.5">
                      {dayItems.slice(0, 3).map(it => (
                        <div key={it.id} className={`text-[9px] truncate px-1 py-0.5 rounded ${it.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : it.status === "invoiced" ? "bg-blue-500/15 text-blue-300" : new Date(it.due_date) < today ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`} title={`${it.program_name}: ${it.title} ${fmtMoneyShort(it.amount)}`}>
                          {fmtMoneyShort(it.amount)}
                        </div>
                      ))}
                      {dayItems.length > 3 && <div className="text-[9px] text-white/50">+{dayItems.length - 3}</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {loading && <div className="mt-3 text-center text-[11px] text-white/40">loading…</div>}
      </div>
    </Card>
  );
}
